import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Placeholders, Scale, SourcePanel } from "../../pages/sa/CapabilityViewModal";
import { Button as UiButton, Modal as UiModal, Pagination } from "../ui";
import {
  CAPABILITY_CATALOG_PAGE_COPY,
  CAPABILITY_CATALOG_SUB_COPY,
  MASTER_LIST_FILTER_ANY,
  formatMasterListCell,
  formatMasterListColumnDisplay,
  formatMasterListQuestionCountLabel,
  isMasterListStatusActive,
  isMasterListStatusColumn,
  type MasterListColumnDef,
  type MasterListColumnKey,
  type MasterListPickerStatus,
  type MasterListQuestionPreview,
  type MasterListRow,
} from "../../utils/masterListUtils";
import {
  CAPABILITY_CATALOG_PAGE_SIZE,
  CATALOG_FRAMEWORK_DUPLICATE_COLUMN_KEYS,
  getCapabilityCatalogColumns,
  getCapabilityCatalogFilterableColumns,
  getCatalogFrameworkGroupKey,
  groupCatalogRowsByFramework,
  isCapabilityCatalogMetricColumn,
  type CapabilityCatalogFilterOptions,
  type CapabilityCatalogFrameworkGroup,
} from "../../utils/capabilityCatalogUtils";

const SUB_CAPABILITY_PAGE_SIZE = 25;

/** Trailing columns: flexible spacer + fixed actions (pushes actions to the right edge). */
const CATALOG_TRAILING_COL_COUNT = 2;

/** Leading checkbox column, present only in selection mode. */
const catalogTableColSpan = (dataColumnCount: number, selectable = false) =>
  dataColumnCount + CATALOG_TRAILING_COL_COUNT + (selectable ? 1 : 0);

/**
 * Optional selection mode (assessment framework builder). Selection state lives
 * with the caller so it survives paging and filtering of the catalog.
 */
export type CapabilityCatalogSelection = {
  selectedCapabilityKeys: ReadonlySet<string>;
  selectedSubKeys: ReadonlySet<string>;
  onToggleCapability: (row: MasterListRow) => void;
  onToggleSub: (sub: MasterListRow) => void;
  onSetCapabilities: (rows: MasterListRow[], selected: boolean) => void;
  onSetSubs: (subs: MasterListRow[], selected: boolean) => void;
  getCapabilityStatus?: (row: MasterListRow) => MasterListPickerStatus | null;
};

const SelectCheckbox = ({
  checked,
  indeterminate = false,
  disabled = false,
  label,
  onChange,
}: {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  label: string;
  onChange: () => void;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [checked, indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className="form-check-input cap-catalog-check"
      checked={checked}
      disabled={disabled}
      aria-label={label}
      title={label}
      onChange={onChange}
      onClick={(event) => event.stopPropagation()}
    />
  );
};

const cx = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(" ");

const catalogColumnClass = (key: MasterListColumnKey) =>
  cx(
    key === "capabilityName" && "cap-catalog-col--primary",
    (key === "capabilityIsActive" ||
      key === "frameworkReferenceCompany" ||
      key === "businessTypeName") &&
      "cap-catalog-col--filter-cluster",
    isCapabilityCatalogMetricColumn(key) && "cap-catalog-col--metric",
    key === "subCapabilityCount" && "cap-catalog-col--metric-first"
  );

const catalogColumnWidthStyle = (width: string | undefined) =>
  width && width !== "auto" ? { width } : undefined;

const isInteractiveTarget = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest("button, a, input, select, textarea, label, .form-check, .master-list-caret")
  );
};

const MasterListCell = ({
  value,
  columnKey,
  clampLines = 2,
  primary = false,
}: {
  value: unknown;
  columnKey?: MasterListColumnKey;
  clampLines?: number;
  primary?: boolean;
}) => {
  const text = columnKey
    ? formatMasterListColumnDisplay(columnKey, value)
    : formatMasterListCell(value);
  if (text === "—" || text === "N/A") {
    return <span className="master-list-empty">{text}</span>;
  }

  return (
    <span
      className={cx(
        "master-list-text",
        clampLines === 1 ? "master-list-text--1l" : "master-list-text--2l",
        primary && "master-list-text--primary"
      )}
      title={text}
    >
      {text}
    </span>
  );
};

const PreviewQuestions = ({ title, rows }: { title: string; rows: MasterListQuestionPreview["current"] }) => (
  <section className="ab-view__card cv-view__section">
    <h3 className="cv-view__title">
      {title} <span className="cv-view__count">{rows.length}</span>
    </h3>
    {rows.length ? (
      <ol className="ab-view__questions">
        {rows.map((row, i) => (
          <li key={row.rowKey}>
            <span className="ab-view__qnum">{i + 1}</span>
            <span>{row.questionText}</span>
          </li>
        ))}
      </ol>
    ) : (
      <p className="ab-wizard__hint">No questions.</p>
    )}
  </section>
);

const QuestionsPreviewModal = ({
  preview,
  onClose,
}: {
  preview: MasterListQuestionPreview | null;
  onClose: () => void;
}) => (
  <UiModal
    open={Boolean(preview)}
    onClose={onClose}
    size="4xl"
    className="sq-view-modal"
    title="Sub-capability"
  >
    {preview ? (
      <div className="ab-view cv-view">
        <section className="ab-view__card ab-view__hero">
          <div className="ab-view__hero-top">
            <div className="ab-view__hero-main">
              <div className="ab-view__title-row">
                <h2 className="ab-view__title">{preview.title || "Questions"}</h2>
                {preview.details && preview.details.active !== undefined ? (
                  <span
                    className={
                      preview.details.active
                        ? "ab-view__status ab-view__status--published"
                        : "ab-view__status"
                    }
                  >
                    {preview.details.active ? "Active" : "Inactive"}
                  </span>
                ) : null}
              </div>
              <p className="ab-view__about cv-view__desc">
                {preview.details?.description?.trim() || preview.subtitle || "No description"}
              </p>
            </div>
          </div>
          <div className="ab-view__stats cv-view__stats">
            <div>
              <p className="ab-wizard__eyebrow">Capability</p>
              <strong className="cv-view__meta-value">{preview.details?.capabilityName || "—"}</strong>
            </div>
            <div>
              <p className="ab-wizard__eyebrow">User type</p>
              <strong className="cv-view__meta-value">{preview.details?.userType || "—"}</strong>
            </div>
            <div>
              <p className="ab-wizard__eyebrow">Contents</p>
              <strong className="cv-view__meta-value">
                {preview.current.length + preview.future.length} questions
              </strong>
            </div>
          </div>
        </section>
        <div className="ab-view__grid tf-view__grid">
          <div className="cv-view__main">
            <PreviewQuestions title="Current-state questions" rows={preview.current} />
            <PreviewQuestions title="Future-state questions" rows={preview.future} />
            {preview.details ? (
              <>
                <Placeholders unit={preview.details.subCapability} />
                <Scale
                  options={preview.details.maturityOptions}
                  custom={Boolean(preview.details.customMaturity)}
                />
              </>
            ) : null}
          </div>
          {preview.details ? (
            <aside className="ab-view__side">
              <SourcePanel
                label={preview.details.frameworkLabel || ""}
                framework={preview.details.framework}
                sourceTypeName={preview.details.sourceTypeName}
              />
            </aside>
          ) : null}
        </div>
      </div>
    ) : null}
  </UiModal>
);

const SubCapabilityCatalogNest = ({
  rows,
  columns,
  onSubRowClick,
  selection,
  renderSubActions,
}: {
  rows: MasterListRow[];
  columns: MasterListColumnDef[];
  onSubRowClick?: (row: MasterListRow) => void;
  selection?: CapabilityCatalogSelection;
  renderSubActions?: (row: MasterListRow) => React.ReactNode;
}) => {
  const selectable = Boolean(selection);
  const selectedCount = selection
    ? rows.filter((row) => selection.selectedSubKeys.has(row.rowKey)).length
    : 0;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const [subPage, setSubPage] = useState(1);
  const subTotalPages = Math.max(1, Math.ceil(rows.length / SUB_CAPABILITY_PAGE_SIZE));

  useEffect(() => {
    setSubPage(1);
  }, [rows]);

  useEffect(() => {
    if (subPage > subTotalPages) {
      setSubPage(subTotalPages);
    }
  }, [subPage, subTotalPages]);

  const pagedSubRows = useMemo(() => {
    const start = (subPage - 1) * SUB_CAPABILITY_PAGE_SIZE;
    return rows.slice(start, start + SUB_CAPABILITY_PAGE_SIZE);
  }, [rows, subPage]);

  const subRangeStart = rows.length === 0 ? 0 : (subPage - 1) * SUB_CAPABILITY_PAGE_SIZE + 1;
  const subRangeEnd = Math.min(subPage * SUB_CAPABILITY_PAGE_SIZE, rows.length);

  return (
  <div className="master-list-nest cap-catalog-sub-tree is-ready">
    {rows.length === 0 ? (
      <p className="master-list-nest__empty">{CAPABILITY_CATALOG_SUB_COPY.empty}</p>
    ) : (
      <>
      {selection ? (
        <div className="cap-catalog-sub-select-bar">
          <label className="cap-catalog-sub-select-bar__all">
            <SelectCheckbox
              checked={allSelected}
              indeterminate={selectedCount > 0}
              label="Select all sub-capabilities"
              onChange={() => selection.onSetSubs(rows, !allSelected)}
            />
            <span>Select all</span>
          </label>
          <span className="cap-catalog-sub-select-bar__count">
            <strong>{selectedCount}</strong> of {rows.length} sub-capabilities selected
          </span>
        </div>
      ) : null}
      <table className="cap-catalog-sub-table" aria-label={CAPABILITY_CATALOG_SUB_COPY.title}>
        <colgroup>
          {selectable ? <col className="cap-catalog-col--select" /> : null}
          {columns.map((column) => (
            <col
              key={column.key}
              className={catalogColumnClass(column.key)}
              style={catalogColumnWidthStyle(column.width)}
            />
          ))}
          <col className="cap-catalog-col--spacer" />
          <col className="master-list-col-actions" />
        </colgroup>
        <tbody>
          {pagedSubRows.map((sub) => {
            const subSelected = Boolean(selection?.selectedSubKeys.has(sub.rowKey));
            const activateSub = selection ? selection.onToggleSub : onSubRowClick;
            return (
            <tr
              key={sub.rowKey}
              id={sub.rowKey}
              className={cx(
                "cap-catalog-sub-row",
                activateSub && "cap-catalog-sub-row--clickable rdm-row-clickable",
                subSelected && "is-selected"
              )}
              role={activateSub && !selection ? "button" : undefined}
              tabIndex={activateSub && !selection ? 0 : undefined}
              onClick={(event) => {
                event.stopPropagation();
                if (isInteractiveTarget(event.target)) return;
                activateSub?.(sub);
              }}
              onKeyDown={(event) => {
                if (!activateSub || selection) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  activateSub(sub);
                }
              }}
            >
              {selection ? (
                <td className="cap-catalog-col--select">
                  <SelectCheckbox
                    checked={subSelected}
                    label={`Select ${sub.subCapabilityName}`}
                    onChange={() => selection.onToggleSub(sub)}
                  />
                </td>
              ) : null}
              {columns.map(({ key, numeric }) => {
                if (key === "capabilityName") {
                  return (
                    <td
                      key={key}
                      className={cx(
                        "master-list-cell",
                        "cap-catalog-col--primary",
                        catalogColumnClass(key)
                      )}
                    >
                      <span className="cap-catalog-sub-row__name">{sub.subCapabilityName}</span>
                    </td>
                  );
                }
                if (key === "questionCount") {
                  const count = Number(sub.questionCount ?? 0);
                  return (
                    <td
                      key={key}
                      className={cx(
                        "master-list-cell",
                        "master-list-col--numeric",
                        "cap-catalog-sub-row__questions"
                      )}
                    >
                      <span
                        className={cx(
                          "master-list-cell-value",
                          "master-list-cell-value--numeric",
                          "cap-catalog-questions-total"
                        )}
                      >
                        {count > 0 ? formatMasterListQuestionCountLabel(count) : "—"}
                      </span>
                    </td>
                  );
                }
                return (
                  <td
                    key={key}
                    className="master-list-cell cap-catalog-sub-row__filler"
                    aria-hidden
                  />
                );
              })}
              <td className="cap-catalog-col--spacer" aria-hidden />
              {renderSubActions ? (
                <td
                  className="master-list-table__actions-col master-list-table__actions-col--catalog"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="master-list-actions-cell master-list-catalog-actions">
                    {renderSubActions(sub)}
                  </div>
                </td>
              ) : (
                <td
                  className="master-list-table__actions-col master-list-table__actions-col--catalog"
                  aria-hidden
                />
              )}
            </tr>
            );
          })}
        </tbody>
      </table>
      {subTotalPages > 1 ? (
        <div className="vd-nested-pagination master-list-nest__pagination">
          <span className="master-list-nest__page-meta">
            Showing {subRangeStart}–{subRangeEnd} of {rows.length}
          </span>
          <Pagination
            page={subPage}
            totalPages={subTotalPages}
            onPageChange={(next) =>
              setSubPage(Math.min(Math.max(1, next), subTotalPages))
            }
          />
        </div>
      ) : null}
      </>
    )}
  </div>
  );
};

export type CapabilityCatalogTableProps = {
  rows: MasterListRow[];
  subsByCapabilityId: Map<number, MasterListRow[]>;
  columnFilters: Record<MasterListColumnKey, string>;
  onColumnFilterChange: (key: MasterListColumnKey, value: string) => void;
  onClearColumnFilters: () => void;
  filterOptionsByKey: CapabilityCatalogFilterOptions;
  page: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onRowClick?: (row: MasterListRow) => void;
  renderActions: (row: MasterListRow) => React.ReactNode;
  onLoadQuestionPreview?: (sub: MasterListRow) => Promise<MasterListQuestionPreview | null>;
  /** Enables checkbox selection of capabilities and sub-capabilities. */
  selection?: CapabilityCatalogSelection;
  /** Actions for sub-capability rows (selection mode). */
  renderSubActions?: (sub: MasterListRow) => React.ReactNode;
  /** Capability row key to bring into view (expands its group/sub list, scrolls, flashes). */
  revealCapabilityKey?: string | null;
  onRevealed?: () => void;
};

export default function CapabilityCatalogTable({
  rows,
  subsByCapabilityId,
  columnFilters,
  onClearColumnFilters,
  page,
  totalPages,
  totalCount,
  onPageChange,
  onRowClick,
  renderActions,
  onLoadQuestionPreview,
  selection,
  renderSubActions,
  revealCapabilityKey,
  onRevealed,
}: CapabilityCatalogTableProps) {
  const selectable = Boolean(selection);
  const columns = useMemo(() => getCapabilityCatalogColumns(), []);
  const filterableColumns = useMemo(() => getCapabilityCatalogFilterableColumns(), []);
  const [expandedCapabilityKeys, setExpandedCapabilityKeys] = useState(() => new Set<string>());
  const [expandedFrameworkKeys, setExpandedFrameworkKeys] = useState(() => new Set<string>());
  const [questionPreview, setQuestionPreview] = useState<MasterListQuestionPreview | null>(null);
  const [questionPreviewLoading, setQuestionPreviewLoading] = useState(false);

  const frameworkGroups = useMemo(() => groupCatalogRowsByFramework(rows), [rows]);

  useEffect(() => {
    const firstKey = frameworkGroups[0]?.key;
    setExpandedFrameworkKeys(firstKey ? new Set([firstKey]) : new Set());
  }, [frameworkGroups]);

  // Declared after the group reset above so a reveal on the same render wins.
  useEffect(() => {
    if (!revealCapabilityKey) return;
    const row = rows.find((item) => item.rowKey === revealCapabilityKey);
    if (!row) return;

    const groupKey = getCatalogFrameworkGroupKey(row);
    setExpandedFrameworkKeys((prev) => (prev.has(groupKey) ? prev : new Set(prev).add(groupKey)));
    if (row.subCapabilityCount > 0) {
      setExpandedCapabilityKeys((prev) =>
        prev.has(row.rowKey) ? prev : new Set(prev).add(row.rowKey)
      );
    }

    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const el = document.getElementById(row.rowKey);
        if (el) {
          const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
          el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
          el.classList.remove("is-flash");
          void el.offsetWidth;
          el.classList.add("is-flash");
          window.setTimeout(() => el.classList.remove("is-flash"), 1400);
        }
        onRevealed?.();
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [frameworkGroups, onRevealed, revealCapabilityKey, rows]);

  /** Selecting a parent capability opens its sub-capabilities so they can be picked. */
  const toggleCapabilitySelection = useCallback(
    (row: MasterListRow) => {
      if (!selection) return;
      const willSelect = !selection.selectedCapabilityKeys.has(row.rowKey);
      selection.onToggleCapability(row);
      if (willSelect && row.subCapabilityCount > 0) {
        setExpandedCapabilityKeys((prev) =>
          prev.has(row.rowKey) ? prev : new Set(prev).add(row.rowKey)
        );
      }
    },
    [selection]
  );

  const pageSelectedCount = selection
    ? rows.filter((row) => selection.selectedCapabilityKeys.has(row.rowKey)).length
    : 0;
  const allPageSelected = rows.length > 0 && pageSelectedCount === rows.length;

  const catalogRangeStart =
    totalCount === 0 ? 0 : (page - 1) * CAPABILITY_CATALOG_PAGE_SIZE + 1;
  const catalogRangeEnd =
    totalCount === 0 ? 0 : Math.min(page * CAPABILITY_CATALOG_PAGE_SIZE, totalCount);

  const goToCatalogPage = useCallback(
    (next: number) => {
      onPageChange(Math.min(Math.max(1, next), totalPages));
    },
    [onPageChange, totalPages]
  );

  const activeFilterCount = useMemo(
    () =>
      filterableColumns.filter(
        ({ key }) => (columnFilters[key] || MASTER_LIST_FILTER_ANY) !== MASTER_LIST_FILTER_ANY
      ).length,
    [columnFilters, filterableColumns]
  );

  const clearFilters = useCallback(() => {
    onClearColumnFilters();
  }, [onClearColumnFilters]);

  const toggleExpanded = useCallback((rowKey: string) => {
    setExpandedCapabilityKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }, []);

  const toggleFrameworkGroup = useCallback((groupKey: string) => {
    setExpandedFrameworkKeys((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  }, []);

  const frameworkDuplicateColumns = useMemo(
    () => new Set<MasterListColumnKey>(CATALOG_FRAMEWORK_DUPLICATE_COLUMN_KEYS),
    []
  );

  const openSubQuestionPreview = useCallback(
    async (sub: MasterListRow) => {
      if (!onLoadQuestionPreview) return;
      setQuestionPreviewLoading(true);
      try {
        const preview = await onLoadQuestionPreview(sub);
        setQuestionPreview(preview);
      } finally {
        setQuestionPreviewLoading(false);
      }
    },
    [onLoadQuestionPreview]
  );

  const closeQuestionPreview = useCallback(() => setQuestionPreview(null), []);

  const renderCapabilityRows = useCallback(
    (group: CapabilityCatalogFrameworkGroup) =>
      group.rows.map((row) => {
        const expanded = expandedCapabilityKeys.has(row.rowKey);
        const childSubs = subsByCapabilityId.get(row.capabilityId) || [];
        const isLeaf = row.subCapabilityCount === 0;
        const hideFrameworkColumns = !group.isUnassigned;
        const selected = Boolean(selection?.selectedCapabilityKeys.has(row.rowKey));
        const pickerStatus = selected ? selection?.getCapabilityStatus?.(row) ?? null : null;

        return (
          <Fragment key={row.rowKey}>
            <tr
              id={row.rowKey}
              className={cx(
                "master-list-row",
                "master-list-row--capability",
                isLeaf ? "master-list-row--capability-leaf" : "master-list-row--capability-parent",
                expanded && "is-open",
                (onRowClick || selection) && "rdm-row-clickable",
                selected && "is-selected"
              )}
              onClick={(event) => {
                if (isInteractiveTarget(event.target)) return;
                if (selection) {
                  toggleCapabilitySelection(row);
                  return;
                }
                onRowClick?.(row);
              }}
            >
              {selection ? (
                <td className="cap-catalog-col--select">
                  <SelectCheckbox
                    checked={selected}
                    label={`Select ${row.capabilityName}`}
                    onChange={() => toggleCapabilitySelection(row)}
                  />
                </td>
              ) : null}
              {columns.map(({ key, sticky, clampLines, numeric }) => {
                const value = row[key];
                const isStatus = isMasterListStatusColumn(key);

                return (
                  <td
                    key={`${row.rowKey}-${key}`}
                    className={cx(
                      "master-list-cell",
                      sticky && "master-list-col--sticky",
                      numeric && "master-list-col--numeric",
                      isMasterListStatusColumn(key) && "master-list-col--status",
                      catalogColumnClass(key)
                    )}
                  >
                    {hideFrameworkColumns && frameworkDuplicateColumns.has(key) ? (
                      <span className="master-list-empty">—</span>
                    ) : key === "capabilityName" ? (
                      <div className="master-list-cap">
                        {isLeaf ? (
                          <button
                            type="button"
                            className="master-list-caret master-list-caret--slot"
                            disabled
                            tabIndex={-1}
                            aria-hidden
                          />
                        ) : (
                          <button
                            type="button"
                            className={cx(
                              "master-list-caret",
                              "is-enabled",
                              expanded && "is-open"
                            )}
                            aria-expanded={expanded}
                            aria-controls={`${row.rowKey}-nest`}
                            title={expanded ? "Hide sub-capabilities" : "Show sub-capabilities"}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleExpanded(row.rowKey);
                            }}
                          >
                            {expanded ? <ChevronDown /> : <ChevronRight />}
                          </button>
                        )}
                        <div className="master-list-cap__text">
                          <div className="master-list-cap-title" title={row.capabilityName}>
                            <span className="master-list-cap-title__text">
                              {row.capabilityName}
                            </span>
                            {isLeaf ? (
                              <span className="cap-catalog-pill cap-catalog-pill--single">
                                Single
                              </span>
                            ) : (
                              <span className="cap-catalog-pill cap-catalog-pill--count">
                                {row.subCapabilityCount}{" "}
                                {row.subCapabilityCount === 1
                                  ? "sub-capability"
                                  : "sub-capabilities"}
                              </span>
                            )}
                            {pickerStatus ? (
                              <span
                                className={cx(
                                  "cap-catalog-pick-status",
                                  `cap-catalog-pick-status--${pickerStatus.tone}`
                                )}
                              >
                                {pickerStatus.label}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ) : isStatus ? (
                      <span
                        className={cx(
                          "cap-catalog-status",
                          isMasterListStatusActive(row, key)
                            ? "cap-catalog-status--active"
                            : "cap-catalog-status--inactive"
                        )}
                      >
                        {formatMasterListCell(row[key])}
                      </span>
                    ) : key === "questionCount" ? (
                      <span
                        className={cx(
                          "master-list-cell-value",
                          "master-list-cell-value--numeric",
                          "cap-catalog-questions-total"
                        )}
                      >
                        {Number(value) > 0 ? `${value} total` : "—"}
                      </span>
                    ) : (
                      <span
                        className={cx(
                          "master-list-cell-value",
                          numeric && "master-list-cell-value--numeric"
                        )}
                      >
                        <MasterListCell
                          value={value}
                          columnKey={key}
                          clampLines={clampLines}
                        />
                      </span>
                    )}
                  </td>
                );
              })}
              <td className="cap-catalog-col--spacer" aria-hidden />
              <td
                className="master-list-table__actions-col master-list-table__actions-col--catalog dash-mobile-table-actions"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="master-list-actions-cell master-list-catalog-actions">
                  {renderActions(row)}
                </div>
              </td>
            </tr>
            {expanded && !isLeaf ? (
              <tr className="master-list-row master-list-row--nest">
                <td
                  colSpan={catalogTableColSpan(columns.length, selectable)}
                  className="master-list-nest-cell"
                >
                  <SubCapabilityCatalogNest
                    rows={childSubs}
                    columns={columns}
                    onSubRowClick={onLoadQuestionPreview ? openSubQuestionPreview : undefined}
                    selection={selection}
                    renderSubActions={renderSubActions}
                  />
                </td>
              </tr>
            ) : null}
          </Fragment>
        );
      }),
    [
      columns,
      expandedCapabilityKeys,
      frameworkDuplicateColumns,
      onLoadQuestionPreview,
      onRowClick,
      openSubQuestionPreview,
      renderActions,
      renderSubActions,
      selectable,
      selection,
      subsByCapabilityId,
      toggleCapabilitySelection,
      toggleExpanded,
    ]
  );

  return (
    <>
      <div className="app-table-wrap master-list-table-wrap cap-catalog-table-wrap">
        <table
          className={cx(
            "table master-list-table dash-table-mobile-skip master-list-table--catalog cap-catalog-table",
            selectable && "cap-catalog-table--selectable"
          )}
        >
          <colgroup>
            {selectable ? <col className="cap-catalog-col--select" /> : null}
            {columns.map((column) => (
              <col
                key={column.key}
                className={catalogColumnClass(column.key)}
                style={catalogColumnWidthStyle(column.width)}
              />
            ))}
            <col className="cap-catalog-col--spacer" />
            <col className="master-list-col-actions" />
          </colgroup>
          <thead>
            <tr className="master-list-head-labels">
              {selection ? (
                <th className="cap-catalog-col--select">
                  <SelectCheckbox
                    checked={allPageSelected}
                    indeterminate={pageSelectedCount > 0}
                    disabled={rows.length === 0}
                    label="Select all capabilities on this page"
                    onChange={() => selection.onSetCapabilities(rows, !allPageSelected)}
                  />
                </th>
              ) : null}
              {columns.map(({ key, label, shortLabel, sticky, numeric }) => (
                <th
                  key={key}
                  className={cx(
                    sticky && "master-list-col--sticky",
                    numeric && "master-list-col--numeric",
                    isMasterListStatusColumn(key) && "master-list-col--status",
                    catalogColumnClass(key)
                  )}
                  title={label}
                >
                  {shortLabel}
                </th>
              ))}
              <th className="cap-catalog-col--spacer" aria-hidden />
              <th className="master-list-table__actions-col master-list-table__actions-col--catalog">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {totalCount === 0 ? (
              <tr>
                <td
                  colSpan={catalogTableColSpan(columns.length, selectable)}
                  className="text-center py-4"
                >
                  <p className="mb-2">{CAPABILITY_CATALOG_PAGE_COPY.empty}</p>
                  {activeFilterCount > 0 ? (
                    <UiButton variant="outline" size="sm" onClick={clearFilters}>
                      Clear filters
                    </UiButton>
                  ) : null}
                </td>
              </tr>
            ) : (
              frameworkGroups.flatMap((group) => {
                const frameworkExpanded = expandedFrameworkKeys.has(group.key);
                const capabilityLabel =
                  group.rows.length === 1 ? "1 capability" : `${group.rows.length} capabilities`;
                const groupSelectedCount = selection
                  ? group.rows.filter((row) => selection.selectedCapabilityKeys.has(row.rowKey))
                      .length
                  : 0;
                const groupAllSelected = groupSelectedCount === group.rows.length;

                return [
                  <tr key={`fw-${group.key}`} className="cap-catalog-fw-row">
                    {selection ? (
                      <td className="cap-catalog-fw-row__cell cap-catalog-col--select">
                        <SelectCheckbox
                          checked={groupAllSelected}
                          indeterminate={groupSelectedCount > 0}
                          label={`Select all capabilities in ${group.title} on this page`}
                          onChange={() => selection.onSetCapabilities(group.rows, !groupAllSelected)}
                        />
                      </td>
                    ) : null}
                    <td colSpan={catalogTableColSpan(columns.length)} className="cap-catalog-fw-row__cell">
                      <button
                        type="button"
                        className="cap-catalog-fw-row__toggle"
                        onClick={() => toggleFrameworkGroup(group.key)}
                        aria-expanded={frameworkExpanded}
                      >
                        <ChevronDown
                          className={cx(
                            "cap-catalog-fw-row__chevron",
                            !frameworkExpanded && "cap-catalog-fw-row__chevron--collapsed"
                          )}
                          size={16}
                          strokeWidth={2}
                          aria-hidden
                        />
                        <div className="cap-catalog-fw-row__text">
                          <span className="cap-catalog-fw-row__title">{group.title}</span>
                          {group.subtitle ? (
                            <span className="cap-catalog-fw-row__subtitle">{group.subtitle}</span>
                          ) : null}
                        </div>
                        <span className="cap-catalog-fw-row__count">{capabilityLabel}</span>
                      </button>
                    </td>
                  </tr>,
                  ...(frameworkExpanded ? renderCapabilityRows(group) : []),
                ];
              })
            )}
          </tbody>
        </table>
      </div>
      {totalCount > 0 ? (
        <div className="master-list-catalog-pagination">
          <span className="master-list-catalog-pagination__meta">
            Showing {catalogRangeStart}–{catalogRangeEnd} of {totalCount} capabilities
            {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ""}
          </span>
          <Pagination page={page} totalPages={totalPages} onPageChange={goToCatalogPage} />
        </div>
      ) : null}
      {questionPreviewLoading ? (
        <Modal show centered onHide={closeQuestionPreview}>
          <Modal.Body className="py-4 text-center text-muted">Loading questions…</Modal.Body>
        </Modal>
      ) : null}
      <QuestionsPreviewModal preview={questionPreview} onClose={closeQuestionPreview} />
    </>
  );
}
